import { DomainValuation } from '@/types';

/**
 * Format currency value with appropriate symbols and formatting
 */
export function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  } else {
    return `$${value.toLocaleString()}`;
  }
}

/**
 * Format valuation range for display
 */
export function formatValuationRange(valuation: DomainValuation): string {
  const { min, max } = valuation.estimatedValue;

  if (min === max) {
    return formatCurrency(min);
  }

  return `${formatCurrency(min)} - ${formatCurrency(max)}`;
}

/**
 * Get confidence level color classes
 */
export function getConfidenceColor(confidence: 'low' | 'medium' | 'high'): string {
  switch (confidence) {
    case 'high':
      return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/20';
    case 'medium':
      return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/20';
    case 'low':
      return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/20';
    default:
      return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/20';
  }
}

/**
 * Get confidence level text
 */
export function getConfidenceText(confidence: 'low' | 'medium' | 'high'): string {
  switch (confidence) {
    case 'high':
      return 'High Confidence';
    case 'medium':
      return 'Medium Confidence';
    case 'low':
      return 'Low Confidence';
    default:
      return 'Unknown';
  }
}

/**
 * Convert score to percentage for progress bars
 */
export function scoreToPercentage(score: number): number {
  return Math.round(score);
}

/**
 * Get factor name for display
 */
export function getFactorName(factor: keyof DomainValuation['factors']): string {
  const names: Record<keyof DomainValuation['factors'], string> = {
    lengthScore: 'Length',
    tldScore: 'TLD Quality',
    memorabilityScore: 'Memorability',
    ageScore: 'Domain Age',
    keywordScore: 'Keywords'
  };
  return names[factor];
}

/**
 * Get factor description for tooltips
 */
export function getFactorDescription(factor: keyof DomainValuation['factors']): string {
  const descriptions: Record<keyof DomainValuation['factors'], string> = {
    lengthScore: 'Shorter domains are generally more valuable and memorable',
    tldScore: 'Premium TLDs like .com, .net, .org have higher commercial value',
    memorabilityScore: 'Easy to pronounce and remember domains are more brandable',
    ageScore: 'Older domains have established trust and SEO value',
    keywordScore: 'Valuable keywords increase commercial potential'
  };
  return descriptions[factor];
}

/**
 * Get valuation tier based on estimated value
 */
export function getValuationTier(valuation: DomainValuation): {
  tier: 'premium' | 'high' | 'medium' | 'low' | 'minimal';
  color: string;
  label: string;
} {
  const avgValue = (valuation.estimatedValue.min + valuation.estimatedValue.max) / 2;

  if (avgValue >= 100000) {
    return {
      tier: 'premium',
      color: 'text-purple-600 dark:text-purple-400',
      label: 'Premium'
    };
  } else if (avgValue >= 10000) {
    return {
      tier: 'high',
      color: 'text-emerald-600 dark:text-emerald-400',
      label: 'High Value'
    };
  } else if (avgValue >= 1000) {
    return {
      tier: 'medium',
      color: 'text-blue-600 dark:text-blue-400',
      label: 'Medium Value'
    };
  } else if (avgValue >= 100) {
    return {
      tier: 'low',
      color: 'text-yellow-600 dark:text-yellow-400',
      label: 'Low Value'
    };
  } else {
    return {
      tier: 'minimal',
      color: 'text-gray-600 dark:text-gray-400',
      label: 'Minimal Value'
    };
  }
}

/**
 * Generate a short summary of the valuation
 */
export function getValuationSummary(valuation: DomainValuation): string {
  const { tier } = getValuationTier(valuation);
  const confidence = getConfidenceText(valuation.confidence);

  return `${tier} domain with ${confidence.toLowerCase()}`;
}

/**
 * Check if valuation data is available and valid
 */
export function isValidValuation(valuation?: DomainValuation): boolean {
  if (!valuation) return false;

  const { min, max } = valuation.estimatedValue;
  return min >= 0 && max >= min && max <= 10000000; // Reasonable upper bound
}
